import { useRef } from 'react'
import { Check, Library, Loader2, Music, Pencil, Sparkles, Upload } from 'lucide-react'
import {
  formatBytes,
  type AudioGroup
} from '../../api'
import type {
  AudioTrackLabelSaveInput,
  AudioTrackLabelSuggestion
} from '../../api/labels'
import type { SunoPrompt } from '../../api/suno'
import { Button, ChipSelect, Modal, ModalActions } from '../../components/ui'
import uploadStyles from '../../components/ui/uploadDialogStyles.module.css'
import { labelCategoryLabels } from '../labels/labelDisplay'
import suggestionStyles from './TrackLabelSuggestions.module.css'

export type AudioUploadItemStatus = 'queued' | 'uploading' | 'assigning' | 'labeling' | 'complete' | 'matched' | 'skipped' | 'failed'

export type AudioUploadItem = {
  id: string
  file: File
  name: string
  progress: number
  size: number
  status: AudioUploadItemStatus
  statusText: string | undefined
}

type AudioUploadDialogProps = {
  acceptedLabels: AudioTrackLabelSaveInput[]
  dragging: boolean
  groups: AudioGroup[]
  items: AudioUploadItem[]
  loadingPromptMatch: boolean
  loadingSuggestions: boolean
  onApplySelectedSuggestions: () => void
  onCheckPromptLibrary: () => void
  onClearPromptMatch: () => void
  onClose: () => void
  onFilesSelected: (files: FileList | null) => void
  onGroupIdsChange: (groupIds: string[]) => void
  onPromptChange: (stylePrompt: string) => void
  onSelectSuggestionIdsChange: (ids: string[]) => void
  onSetDragging: (dragging: boolean) => void
  onSuggestLabels: () => void
  promptMatch: SunoPrompt | null
  promptMatchError: string | null
  selectedGroupIds: string[]
  selectedSuggestionIds: string[]
  stylePrompt: string
  suggestionError: string | null
  suggestions: AudioTrackLabelSuggestion[]
  uploading: boolean
}

export const AudioUploadDialog = ({
  acceptedLabels,
  dragging,
  groups,
  items,
  loadingPromptMatch,
  loadingSuggestions,
  onApplySelectedSuggestions,
  onCheckPromptLibrary,
  onClearPromptMatch,
  onClose,
  onFilesSelected,
  onGroupIdsChange,
  onPromptChange,
  onSelectSuggestionIdsChange,
  onSetDragging,
  onSuggestLabels,
  promptMatch,
  promptMatchError,
  selectedGroupIds,
  selectedSuggestionIds,
  stylePrompt,
  suggestionError,
  suggestions,
  uploading
}: AudioUploadDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const uploadClass = (...classNames: Array<string | false | null | undefined>): string =>
    classNames
      .filter(Boolean)
      .map((className) => uploadStyles[className as string] ?? className)
      .join(' ')
  const suggestionClass = (...classNames: Array<string | false | null | undefined>): string =>
    classNames.filter(Boolean).join(' ')

  const toggleGroup = (groupId: string): void => {
    onGroupIdsChange(
      selectedGroupIds.includes(groupId)
        ? selectedGroupIds.filter((id) => id !== groupId)
        : [...selectedGroupIds, groupId]
    )
  }

  const toggleSuggestion = (labelId: string): void => {
    onSelectSuggestionIdsChange(
      selectedSuggestionIds.includes(labelId)
        ? selectedSuggestionIds.filter((id) => id !== labelId)
        : [...selectedSuggestionIds, labelId]
    )
  }

  const uploadItemsList = (variant: 'dropzone' | 'standalone') => (
    <span className={uploadClass('upload-list', variant === 'dropzone' && 'upload-drop-list')}>
      {items.map((item) => (
        <span className={uploadStyles['upload-item']} key={item.id}>
          <span className={uploadStyles['upload-item-icon']}>
            <Music size={17} />
          </span>
          <span className={uploadStyles['upload-item-info']}>
            <span className={uploadStyles['upload-item-name']}>{item.name}</span>
            <span className={uploadStyles['upload-item-size']}>{formatBytes(item.size)}</span>
            {item.status === 'matched' && item.statusText && (
              <span className={uploadStyles['upload-item-note']}>{item.statusText}</span>
            )}
            <span className={uploadStyles['upload-progress']}>
              <span style={{ width: `${item.progress}%` }} />
            </span>
          </span>
          <span className={uploadClass('upload-status', item.status)} title={item.statusText}>
            {item.status === 'queued' && 'Queued'}
            {item.status === 'uploading' && `${item.progress}%`}
            {item.status === 'assigning' && 'Assigning'}
            {item.status === 'labeling' && 'Labeling'}
            {item.status === 'complete' && 'Complete'}
            {item.status === 'matched' && 'Matched'}
            {item.status === 'skipped' && 'Skipped'}
            {item.status === 'failed' && 'Failed'}
          </span>
        </span>
      ))}
    </span>
  )

  const promptMatchStatus = (className?: string) => (
    (loadingPromptMatch || promptMatch || promptMatchError) && (
      <span className={uploadClass('upload-prompt-match', promptMatch && 'found', className)}>
        {loadingPromptMatch && 'Checking Prompt Library...'}
        {!loadingPromptMatch && promptMatch && (
          <>
            <div className={uploadStyles['upload-prompt-match-content']}>
              <strong>Library prompt found: {promptMatch.title}</strong>
              {promptMatch.description && <em>{promptMatch.description}</em>}
            </div>
            <button className={uploadStyles['upload-prompt-match-edit']} onClick={onClearPromptMatch} type="button">
              <Pencil size={14} />
            </button>
          </>
        )}
        {!loadingPromptMatch && promptMatchError && promptMatchError}
      </span>
    )
  )

  const labelSuggestions = (className?: string) => (
    <>
      {suggestionError && (
        <div className={suggestionStyles.message}>{suggestionError}</div>
      )}
      {suggestions.length > 0 && (
        <div className={suggestionClass(suggestionStyles.list, uploadStyles['upload-suggestion-list'], className)} aria-label="Upload LLM label suggestions">
          {suggestions.map((suggestion) => {
            const selected = selectedSuggestionIds.includes(suggestion.label_id)

            return (
              <button
                className={suggestionClass(suggestionStyles.item, selected && suggestionStyles.selected)}
                key={suggestion.label_id}
                onClick={() => toggleSuggestion(suggestion.label_id)}
                type="button"
              >
                <span className="track-suggestion-check" aria-hidden="true">
                  {selected && <Check size={13} />}
                </span>
                <span className={suggestionStyles.copy}>
                  <span>
                    <strong>{suggestion.label.name}</strong>
                    <span>{labelCategoryLabels[suggestion.label.category]}</span>
                  </span>
                  <em>{suggestion.reason}</em>
                </span>
                <span className={suggestionStyles.score}>
                  {Math.round(suggestion.confidence * 100)}%
                </span>
              </button>
            )
          })}
          <div className={suggestionStyles.actions}>
            <Button
              onClick={() => onSelectSuggestionIdsChange(suggestions.map((suggestion) => suggestion.label_id))}
            >
              Select all
            </Button>
            <Button
              disabled={selectedSuggestionIds.length === 0}
              onClick={onApplySelectedSuggestions}
              variant="primary"
            >
              Apply to upload batch
            </Button>
          </div>
        </div>
      )}
    </>
  )

  const groupChips = (className?: string) => (
    <ChipSelect
      chipClassName={uploadStyles.uploadChip}
      className={uploadClass('audio-upload-chip-select', className)}
      empty={<span className="muted">Create a group first to enable upload assignment.</span>}
      onToggle={toggleGroup}
      options={groups.map((group) => ({ id: group.id, label: group.name }))}
      selectedIds={selectedGroupIds}
    />
  )

  return (
    <Modal
      ariaLabelledBy="upload-audio-title"
      bodyClassName={uploadStyles.uploadSheetBody ?? ''}
      closeLabel="Close"
      mobilePresentation="sheet"
      onClose={onClose}
      panelClassName={uploadClass('upload-modal-panel', 'audio-upload-panel')}
      title={<h2 id="upload-audio-title">Upload Audio</h2>}
    >
      <input
        accept="audio/*"
        hidden
        multiple
        onChange={(event) => {
          onFilesSelected(event.target.files)
          event.currentTarget.value = ''
        }}
        ref={fileInputRef}
        type="file"
      />

      <div className={uploadStyles.desktopUploadContent}>
        <div
          aria-label="Drop audio files here or click to browse"
          className={uploadClass('upload-dropzone', dragging && 'dragover', items.length > 0 && 'has-items')}
          onClick={() => fileInputRef.current?.click()}
          onDragLeave={() => onSetDragging(false)}
          onDragOver={(event) => {
            event.preventDefault()
            onSetDragging(true)
          }}
          onDrop={(event) => {
            event.preventDefault()
            onSetDragging(false)
            onFilesSelected(event.dataTransfer.files)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              fileInputRef.current?.click()
            }
          }}
          role="button"
          tabIndex={0}
        >
          <span className={uploadStyles['upload-drop-copy']}>
            <Upload size={items.length > 0 ? 24 : 34} />
            <span className={uploadClass('upload-drop-title', 'upload-copy-desktop')}>
              {items.length > 0 ? 'Add more audio files' : 'Drag and drop files here'}
            </span>
            <span className={uploadClass('upload-drop-title', 'upload-copy-mobile')}>
              {items.length > 0 ? 'Add audio files' : 'Choose audio files'}
            </span>
            <span className={uploadClass('upload-drop-subtitle', 'upload-copy-desktop')}>or click to browse - supports MP3, WAV, FLAC, OGG, AAC</span>
            <span className={uploadClass('upload-drop-subtitle', 'upload-copy-mobile')}>MP3, WAV, FLAC, OGG, AAC</span>
          </span>
          {items.length > 0 && uploadItemsList('dropzone')}
        </div>
        <label className="form-group">
          {!promptMatch && (
            <>
              <span className={uploadStyles['upload-prompt-label-row']}>
                <span className="form-label">Suno style prompt (optional)</span>
                <Button
                  className={uploadClass('upload-check-library-button')}
                  disabled={!stylePrompt.trim() || loadingPromptMatch}
                  onClick={onCheckPromptLibrary}
                >
                  {loadingPromptMatch ? <Loader2 className="tools-spin" size={16} /> : <Library size={16} />}
                  Check library
                </Button>
              </span>
              <textarea
                className="form-input"
                onChange={(event) => onPromptChange(event.target.value)}
                placeholder="One prompt for all files in this upload batch"
                value={stylePrompt}
              />
              <span className="form-label-hint">Use one prompt for this upload batch, then review and apply suggested labels.</span>
            </>
          )}
          {promptMatchStatus()}
        </label>
        <div className={uploadStyles['upload-label-suggestions']}>
          <div className={uploadStyles['upload-label-suggestion-header']}>
            <div>
              <strong>LLM label suggestions</strong>
              <span>{acceptedLabels.length > 0 ? `${acceptedLabels.length} label(s) will be applied to uploaded tracks.` : 'Suggestions are temporary until applied.'}</span>
            </div>
            <Button
              disabled={!stylePrompt.trim() || loadingSuggestions}
              mobileFullWidth
              onClick={onSuggestLabels}
            >
              <Sparkles size={16} /> {loadingSuggestions ? 'Suggesting...' : 'Suggest labels'}
            </Button>
          </div>
          {labelSuggestions()}
        </div>
        <div className="form-group">
          <span className="form-label">Assign to Audio Groups (optional)</span>
          {groupChips()}
        </div>
        <ModalActions className={uploadClass('uploadDoneActions')} layout="stretch">
          <Button disabled={uploading} fullWidth icon={<Check size={16} />} onClick={onClose} variant="primary">
            Done
          </Button>
        </ModalActions>
      </div>

      <div className={uploadStyles.mobileUploadContent}>
        <section className={uploadStyles.mobileUploadSection}>
          <div className={uploadStyles.mobileSectionHeader}>
            <span>Files</span>
            <strong>{items.length > 0 ? `${items.length} selected` : 'Choose audio'}</strong>
          </div>
          <button
            className={uploadClass('mobile-file-picker', dragging && 'dragover')}
            onClick={() => fileInputRef.current?.click()}
            onDragLeave={() => onSetDragging(false)}
            onDragOver={(event) => {
              event.preventDefault()
              onSetDragging(true)
            }}
            onDrop={(event) => {
              event.preventDefault()
              onSetDragging(false)
              onFilesSelected(event.dataTransfer.files)
            }}
            type="button"
          >
            <Upload size={22} />
            <span>{items.length > 0 ? 'Add more files' : 'Select audio files'}</span>
            <small>MP3, WAV, FLAC, OGG, AAC</small>
          </button>
          {items.length > 0 && uploadItemsList('standalone')}
        </section>

        <section className={uploadStyles.mobileUploadSection}>
          <div className={uploadStyles.mobileSectionHeader}>
            <span>Prompt</span>
            <strong>{promptMatch ? 'Matched' : stylePrompt.trim() ? 'Ready' : 'Optional'}</strong>
          </div>
          {!promptMatch && (
            <>
              <textarea
                className="form-input"
                onChange={(event) => onPromptChange(event.target.value)}
                placeholder="One Suno style prompt for this upload batch"
                value={stylePrompt}
              />
              <Button
                className={uploadClass('mobile-section-button')}
                disabled={!stylePrompt.trim() || loadingPromptMatch}
                onClick={onCheckPromptLibrary}
              >
                {loadingPromptMatch ? <Loader2 className="tools-spin" size={16} /> : <Library size={16} />}
                Check library
              </Button>
            </>
          )}
          {promptMatchStatus(uploadStyles.mobilePromptMatch)}
        </section>

        <section className={uploadStyles.mobileUploadSection}>
          <div className={uploadStyles.mobileSectionHeader}>
            <span>Labels</span>
            <strong>
              {acceptedLabels.length > 0
                ? `${acceptedLabels.length} applied`
                : suggestions.length > 0
                  ? `${suggestions.length} suggested`
                  : 'Suggest'}
            </strong>
          </div>
          <Button
            className={uploadClass('mobile-section-button')}
            disabled={!stylePrompt.trim() || loadingSuggestions}
            onClick={onSuggestLabels}
          >
            <Sparkles size={16} /> {loadingSuggestions ? 'Suggesting...' : 'Suggest labels'}
          </Button>
          {acceptedLabels.length > 0 && (
            <p className={uploadStyles.mobileSectionHint}>{acceptedLabels.length} label(s) will be applied to uploaded tracks.</p>
          )}
          {labelSuggestions(uploadStyles.mobileSuggestionList)}
        </section>

        <section className={uploadStyles.mobileUploadSection}>
          <div className={uploadStyles.mobileSectionHeader}>
            <span>Groups</span>
            <strong>{selectedGroupIds.length > 0 ? `${selectedGroupIds.length} selected` : 'Optional'}</strong>
          </div>
          {groupChips(uploadStyles.mobileGroupChips)}
        </section>

        <ModalActions className={uploadClass('uploadDoneActions')} layout="stretch">
          <Button disabled={uploading} fullWidth icon={<Check size={16} />} onClick={onClose} variant="primary">
            Done
          </Button>
        </ModalActions>
      </div>
    </Modal>
  )
}
